import React, { useMemo } from 'react';
import GearItemCard from '../gear/GearItemCard';
import PlayerProjectionSummary from './PlayerProjectionSummary';
import './PlayerGearProjectionTab.css';

/**
 * PLAYER PAGE — GEAR TAB
 *
 * Replaces the old read-only gear list (which could only deep-link across to
 * GearPage) with a self-contained loadout editor plus a projected-score preview,
 * so a manager can see what a gear change is worth WITHOUT leaving the player.
 *
 * LAYOUT (top to bottom), per the approved mockup:
 *   1. Projected score breakdown — same format as the PTS tab
 *   2. HELD    — this player's equipped gear
 *   3. UNHELD  — scrollable list of unequipped team gear
 *
 * (The mockup also showed Autoequip / Clear buttons above the breakdown. They
 * were built, then removed — see the block comment at the bottom of this
 * header for why, and do not re-add them without reading it.)
 *
 * GEAR CARD BEHAVIOUR IS DELIBERATELY IDENTICAL TO GearPage. Both the HELD and
 * UNHELD lists render the same `GearItemCard` GearPage uses, so tap targets,
 * colours, the equip/unequip modal and slot-limit messaging are literally the
 * same component. This screen supplies GearPage's callbacks; it does not
 * reimplement them, and no gear semantics are duplicated here.
 *
 * TRANSFER MODE IS INTENTIONALLY NOT WIRED UP. GearPage's swap/transfer flow is
 * a cross-player interaction that needs every player's accordion visible to
 * make sense. This tab shows exactly one player, so `GearItemCard` is rendered
 * without transfer props — it then renders its badge as an ordinary, inert
 * label. Cross-player moves remain GearPage's job.
 *
 * ⚠️ NO AUTOEQUIP / CLEAR — AND DO NOT RE-ADD THEM WITHOUT READING THIS.
 *
 * An earlier revision had bulk Autoequip/Clear buttons. They were removed
 * because the frontend cannot currently predict what a multi-item equip will
 * produce:
 *
 *   `GearEquipService.equip()` performs DISPLACEMENT-BY-SLOT for every non-ANY
 *   item — it unequips *every* existing item sharing the candidate's slot
 *   (gear_equip.py, "if persisted_gear.slot != 'ANY'"). So equipping two HANDS
 *   items sequentially does not yield two equipped HANDS items; the second
 *   displaces the first, and the batch silently ends with fewer items than the
 *   user was shown.
 *
 * `EQUIPMENT_LIMITS.HANDS = 2` in `utils/gearRules` therefore describes a state
 * the equip endpoint cannot actually reach. That divergence is PRE-EXISTING and
 * shared with GearPage; it has been reported to engineering rather than patched
 * here, because deciding whether the limit or the endpoint is wrong is a
 * product/backend call, not a frontend one.
 *
 * Single-item equip/unequip through `GearItemCard` is unaffected: one item, one
 * request, and the server's displacement is immediately reflected by the
 * refetch that follows.
 */
function PlayerGearProjectionTab({
    player,
    teamGear,
    gearLoadError = null,
    /**
     * ⭐ THE PROJECTION IS OWNED BY `PlayerPage`, NOT COMPUTED HERE.
     *
     * This tab used to call `buildProjectionSummary()` itself while the page
     * called it separately for the always-visible PROJ bar. Two calls meant
     * two chances to differ — and the adapter is now an async server read, so
     * they could genuinely resolve against different loadouts mid-equip and
     * put two different totals on one screen, which is the exact complaint
     * this feature exists to answer.
     *
     * The page performs the single read and hands the result to both
     * surfaces. `projectionPending` and `projectionError` travel with it
     * because "loading", "failed to read", and "this player has no
     * projection" are three different facts that must not render alike.
     */
    projectionSummary = null,
    projectionPending = false,
    projectionError = null,
    canModifyGear,
    // Distinguishes "the week is open/closed" from "we never learned which".
    // Defaults TRUE only so existing callers that already resolved the status
    // keep their copy; `canModifyGear` is the actual gate and is computed by
    // the page, which fails closed on its own.
    weekStatusKnown = true,
    onEquip,
    onUnequip,
    isOpponentView,
}) {
    /**
     * The mutation target. `character_sheet_id` ONLY — never `player.id`.
     *
     * ⚠️ DO NOT ADD A FALLBACK HERE. An earlier revision read
     * `player?.character_sheet_id ?? player?.id`, which is not a graceful
     * degradation but a silent identity substitution:
     *
     *   • These are PKs of DIFFERENT TABLES. On a roster row `player.id` is a
     *     `LeaguePlayer` PK while `character_sheet_id` is a `CharacterSheet`
     *     PK. The two sequences overlap numerically, so a LeaguePlayer id is
     *     very likely to be a VALID CharacterSheet id — just somebody else's.
     *   • `/v2/gear/<id>/equip/` takes `character_sheet_id` and resolves it
     *     server-side (`GearAssignmentSerializer.validate_character_sheet_id`).
     *     A wrong-but-valid id therefore does not 400 — it succeeds, and the
     *     item lands on a different player's sheet.
     *   • The fallback also defeated the page's own guard. `handleEquipGear`
     *     refuses a null/undefined id, but the fallback made sure it never saw
     *     one, converting a loud, correct failure into a silent mis-equip.
     *
     * A missing sheet id means the roster payload is malformed. That is a
     * fail-closed state (see the malformed-data branch below), not a state to
     * paper over with a different table's key.
     */
    const characterSheetId = player?.character_sheet_id ?? null;
    const characterSheetIdKnown = characterSheetId !== null;

    /**
     * Split team gear into this player's equipped items and the unheld pool.
     *
     * `character_sheet` is the authoritative ownership field on a Gear row —
     * null means unequipped. Items equipped to OTHER players are excluded from
     * both lists: they are neither this player's loadout nor available to take
     * without an explicit cross-player transfer on GearPage.
     */
    const { heldGear, unheldGear } = useMemo(() => {
        const held = [];
        const unheld = [];

        (teamGear || []).forEach((item) => {
            if (item.character_sheet == null) {
                unheld.push(item);
            } else if (item.character_sheet === characterSheetId) {
                held.push(item);
            }
        });

        return { heldGear: held, unheldGear: unheld };
    }, [teamGear, characterSheetId]);

    /**
     * What `PlayerProjectionSummary` actually renders.
     *
     * The page owns the projection (see the prop docs above); this only maps
     * the page's three possible non-summary states onto the `available: false`
     * shape the summary component requires — it throws on a bare null, by
     * design, so that a missing projection is explained rather than silently
     * blank.
     *
     * ⚠️ THE THREE STATES ARE NOT INTERCHANGEABLE AND MUST NOT SHARE COPY:
     *
     *   • **pending** — "Loading projection data…". NOT "unavailable", which
     *     would be a false claim about data that is simply still in flight.
     *   • **error** — the read FAILED. Says so, and says the loadout below is
     *     still real. Rendering this as "no projection for this player" would
     *     make an outage indistinguishable from the legitimate tier gaps
     *     (QB LOW, WR 13–14), which is the silent-failure pattern AGENTS.md
     *     forbids.
     *   • **summary** — render it, including its own `available: false` case,
     *     which is the server's honest "no tier data" answer.
     */
    const renderedProjection = useMemo(
        () => {
            if (projectionError) {
                return {
                    available: false,
                    reason:
                        'The projection could not be loaded. This is a failure to ' +
                        'read it — not a player without a projection. Your gear ' +
                        'below is unaffected and still accurate.',
                    unavailableBecauseOpponentView: false,
                    basePoints: null,
                    gearPoints: null,
                    totalPoints: null,
                    estimatedStats: null,
                    gearedStats: null,
                    statLineChanged: null,
                    gearItems: [],
                    unknownGearCount: heldGear.length,
                    totalIsPartial: false,
                    source: null,
                };
            }

            if (projectionPending || !projectionSummary) {
                return {
                    available: false,
                    reason: 'Loading projection data…',
                    unavailableBecauseOpponentView: false,
                    basePoints: null,
                    gearPoints: null,
                    totalPoints: null,
                    estimatedStats: null,
                    gearedStats: null,
                    statLineChanged: null,
                    gearItems: [],
                    unknownGearCount: heldGear.length,
                    totalIsPartial: false,
                    source: null,
                };
            }

            return projectionSummary;
        },
        [projectionSummary, projectionPending, projectionError, heldGear]
    );

    /**
     * Whether the gear cards may be interacted with.
     *
     * Single-item equip/unequip only — there is no bulk path to coordinate
     * with, so this is "may this user change gear right now, AND do we know
     * whose sheet the change would land on?". Without a resolved
     * `character_sheet_id` there is no answerable equip target, so the cards
     * stay inert rather than aiming at a guess.
     */
    const cardsInteractive = canModifyGear && !isOpponentView && characterSheetIdKnown;

    // ================= GEAR INVENTORY UNREADABLE =================
    // Rendered INSTEAD of the editor, never alongside it.
    //
    // "This team owns no gear" and "we could not read this team's gear" are
    // different facts and must never look the same. If the inventory failed to
    // load, the HELD/UNHELD lists below would render "No gear equipped" and
    // "All gear is equipped" — two confident, specific, and FALSE claims about
    // a loadout we cannot actually see. A manager could act on that, believing
    // gear they have equipped is missing.
    //
    // The editor is withheld entirely rather than shown in a degraded state,
    // because its controls reason about which items are held and would
    // otherwise target the wrong set.
    if (gearLoadError) {
        return (
            <div className="player-gear-tab">
                <div className="player-gear-load-error" role="alert">
                    <div className="player-gear-load-error-title">
                        ⚠️ Gear could not be loaded
                    </div>
                    <div className="player-gear-load-error-body">
                        Your gear inventory could not be read, so this player's
                        loadout is <strong>unknown</strong> right now. It has NOT
                        been cleared — nothing shown here reflects your real gear.
                    </div>
                    <div className="player-gear-load-error-hint">
                        Reopen this tab or reload the page to try again. If it keeps
                        failing, your session may have expired.
                    </div>
                    {gearLoadError.message && (
                        <div className="player-gear-load-error-detail">
                            {gearLoadError.message}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ================= NO CHARACTER SHEET =================
    // Rendered INSTEAD of the editor, for the same reason as the branch above:
    // a confident, specific, FALSE claim is worse than an explicit unknown.
    //
    // Without a `character_sheet_id` we cannot identify this player's rows in
    // the team inventory. HELD would render "No gear equipped" — a claim about
    // a loadout we cannot see — and every UNHELD card would offer an equip
    // whose destination we cannot name.
    //
    // This is fail-closed by design. The tempting "fix" is to fall back to
    // `player.id`, which produces a plausible-looking screen that equips the
    // WRONG player; see the note on `characterSheetId` above.
    if (!characterSheetIdKnown) {
        console.error(
            '❌ PlayerGearProjectionTab: roster payload has no character_sheet_id; ' +
            'gear cannot be attributed or modified for this player.',
            { playerId: player?.id, playerName: player?.name }
        );

        return (
            <div className="player-gear-tab">
                <div className="player-gear-load-error" role="alert">
                    <div className="player-gear-load-error-title">
                        ⚠️ Gear is unavailable for this player
                    </div>
                    <div className="player-gear-load-error-body">
                        This player's record is missing its character sheet, so we
                        cannot tell which gear belongs to them. Their loadout is{' '}
                        <strong>unknown</strong> — it has NOT been cleared.
                    </div>
                    <div className="player-gear-load-error-hint">
                        Gear changes are disabled here to avoid equipping the wrong
                        player. Other players on your team are unaffected, and the
                        Gear page can still be used.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="player-gear-tab">
            {/* Explain a disabled state rather than leaving dead controls
                unexplained — and say WHICH disabled state it is. "Locked
                because the week is scoring" and "we could not confirm whether
                it is safe to edit" are different situations, and telling a user
                their week is scoring when we simply failed to read the status
                would be a confident false claim. */}
            {!canModifyGear && !isOpponentView && (
                <div className="player-gear-locked-note">
                    {weekStatusKnown
                        ? '🔒 Gear changes are locked while the week is scoring.'
                        : '🔒 Gear changes are disabled because the league\'s week status ' +
                          'could not be confirmed. Reload to try again.'}
                </div>
            )}

            {/* ============ PROJECTED SCORE ============ */}
            <PlayerProjectionSummary summary={renderedProjection} />

            {/* ============ HELD ============ */}
            <div className="player-gear-section">
                <div className="player-gear-section-header">
                    <span className="player-gear-section-pill">HELD</span>
                </div>
                <div className="player-gear-section-body">
                    {heldGear.length === 0 ? (
                        <div className="player-gear-empty">No gear equipped</div>
                    ) : (
                        heldGear.map((item, index) => (
                            <GearItemCard
                                key={item.id}
                                gear={item}
                                gearNumber={index + 1}
                                canModifyGear={cardsInteractive}
                                onUnequip={onUnequip}
                                teamGear={teamGear}
                            />
                        ))
                    )}
                </div>
            </div>

            {/* ============ UNHELD (scrollable) ============

                ⚠️ OPPONENT VIEW RENDERS NO UNHELD SECTION AT ALL.

                `teamGear` here is whatever inventory the page loaded. In an
                opponent view that is the OPPONENT's team, so an "unequipped
                gear" list exposes their bench inventory — information a manager
                has no business reading off a rival's player sheet, and which
                nothing on this screen asks for. The HELD list is different and
                is deliberately kept: what an opponent has EQUIPPED is visible
                competitive information, and `PlayerProjectionSummary` already
                promises "their equipped gear is still listed below".

                It would also be a pile of dead controls even if it were
                harmless: `cardsInteractive` is false in opponent view, so every
                card would be inert, and each one's equip modal targets
                `[player]` — the opponent — which is not a thing this user may
                equip to. Rendering a list whose every action is impossible
                invites the question "why is this greyed out?" and answers it
                nowhere. */}
            {!isOpponentView && (
                <div className="player-gear-section">
                    <div className="player-gear-section-header">
                        <span className="player-gear-section-pill">UNHELD</span>
                    </div>
                    <div className="player-gear-section-body player-gear-section-body--scroll">
                        {unheldGear.length === 0 ? (
                            <div className="player-gear-empty">All gear is equipped</div>
                        ) : (
                            unheldGear.map((item, index) => (
                                <GearItemCard
                                    key={item.id}
                                    gear={item}
                                    gearNumber={index + 1}
                                    canModifyGear={cardsInteractive}
                                    onEquip={onEquip}
                                    // Single-element list: the equip modal offers only
                                    // THIS player, which is the whole point of equipping
                                    // from inside a player's own sheet.
                                    teamPlayers={player ? [player] : []}
                                    teamGear={teamGear}
                                />
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default PlayerGearProjectionTab;
